import React, {forwardRef, useEffect, useRef, useState} from "react";
import Hls from "hls.js";
import {createUseStyles} from "react-jss";
import {VideoControls} from "./VideoControls";

const useStyles = createUseStyles({
    videoPlayer: {
        maskImage: "-webkit-radial-gradient(white, black)",
        backfaceVisibility: "hidden"
    },
    loaderOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.2)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        transition: "opacity 300ms ease, visibility 300ms ease",
        zIndex: 5,
        pointerEvents: "none",
        opacity: 1,
        visibility: "visible",
    },
    loaderHidden: {
        opacity: 0,
        visibility: "hidden",
    },
    spinnerRing: {
        position: "relative",
        width: "50px",
        height: "50px",
        borderRadius: "50%",
        background: "linear-gradient(45deg, transparent, transparent 40%, #ffffff)",
        animation: "$spin 0.8s linear infinite",
        boxShadow: "0 0 10px rgba(255, 255, 255, 0.2)",
        "&::after": {
            content: '""',
            position: "absolute",
            top: "3px",
            left: "3px",
            right: "3px",
            bottom: "3px",
            background: "rgba(20, 20, 20, 0.8)",
            borderRadius: "50%",
        },
        "&::before": {
            content: '""',
            position: "absolute",
            width: "5px",
            height: "5px",
            background: "#ffffff",
            boxShadow: "0 0 8px #ffffff, 0 0 15px #ffffff",
            borderRadius: "50%",
            top: "4px",
            right: "4px",
            zIndex: 1,
        }
    },
    "@keyframes spin": {
        "0%": { transform: "rotate(0deg)" },
        "100%": { transform: "rotate(360deg)" }
    }
})

const DOCUMENT_POSITION_PRECEDING = typeof Node !== "undefined" ? Node.DOCUMENT_POSITION_PRECEDING : 2;
const DOCUMENT_POSITION_FOLLOWING = typeof Node !== "undefined" ? Node.DOCUMENT_POSITION_FOLLOWING : 4;

let playerStates = [];

const sortPlayers = () => {
    playerStates.sort((a, b) => {
        const elA = a.element;
        const elB = b.element;
        if (!elA || !elB) return 0;
        if (elA === elB) return 0;
        const position = elA.compareDocumentPosition(elB);
        if (position & DOCUMENT_POSITION_FOLLOWING) {
            return -1;
        }
        if (position & DOCUMENT_POSITION_PRECEDING) {
            return 1;
        }
        return 0;
    });
};

const updatePreloads = () => {
    Promise.resolve().then(() => {
        sortPlayers();
        
        let activePlayerBuffering = false;
        for (let i = 0; i < playerStates.length; i++) {
            const player = playerStates[i];
            if (player.intersecting && !player.isLoaded) {
                activePlayerBuffering = true;
                break;
            }
        }

        for (let i = 0; i < playerStates.length; i++) {
            const player = playerStates[i];
            
            let inPreloadQueue = false;
            if (player.intersecting) {
                inPreloadQueue = true;
            } else if (i > 0 && playerStates[i - 1].isLoaded) {
                inPreloadQueue = true;
            }
            
            if (inPreloadQueue && !player.canLoad) {
                player.canLoad = true;
                player.setCanLoad(true);
            }

            let shouldDownload = false;
            if (player.intersecting) {
                shouldDownload = true;
            } else if (inPreloadQueue && !activePlayerBuffering) {
                shouldDownload = true;
            }

            if (player.shouldDownload !== shouldDownload) {
                player.shouldDownload = shouldDownload;
                player.setShouldDownload(shouldDownload);
            }
        }
    });
};

const registerPlayer = (id, element, setCanLoad, setShouldDownload) => {
    const player = {
        id,
        element,
        setCanLoad,
        setShouldDownload,
        canLoad: false,
        shouldDownload: true,
        isLoaded: false,
        intersecting: false
    };
    playerStates.push(player);
    updatePreloads();
    return player;
};

const unregisterPlayer = (id) => {
    playerStates = playerStates.filter(p => p.id !== id);
    updatePreloads();
};

const updatePlayerStatus = (id, { intersecting, isLoaded }) => {
    const player = playerStates.find(p => p.id === id);
    if (player) {
        let changed = false;
        if (intersecting !== undefined && player.intersecting !== intersecting) {
            player.intersecting = intersecting;
            changed = true;
        }
        if (isLoaded !== undefined && player.isLoaded !== isLoaded) {
            player.isLoaded = isLoaded;
            changed = true;
        }
        if (changed) {
            updatePreloads();
        }
    }
};

export const VideoPlayer = forwardRef(
    (
        {
            autoPlay = false,
            videoUrl,
            poster,
            onLoadedMetadata,
            muted = false,
            controls = false,
            loop = false,
            onEnded = null,
            inView = false
        },
        playerRef
    ) => {
        const [playlist, setPlaylist] = useState(null);
        const [hideCustomUnmute, setHideCustomUnmute] = useState(false)
        const [hasAudio, setHasAudio] = useState(true)

        const classes = useStyles()

        const playerIdRef = useRef(Math.random().toString(36).substr(2, 9));
        const [canLoad, setCanLoad] = useState(false);
        const [shouldDownload, setShouldDownload] = useState(true);
        const [isLoading, setIsLoading] = useState(true);
        const [showSpinner, setShowSpinner] = useState(false);
        const [localIntersecting, setLocalIntersecting] = useState(false);
        const containerRef = useRef(null);
        const [hlsInstance, setHlsInstance] = useState(null);

        useEffect(() => {
            const id = playerIdRef.current;
            registerPlayer(id, containerRef.current, setCanLoad, setShouldDownload);
            return () => {
                unregisterPlayer(id);
            };
        }, []);

        useEffect(() => {
            const isLoaded = canLoad && !isLoading;
            updatePlayerStatus(playerIdRef.current, { isLoaded });
        }, [canLoad, isLoading]);

        useEffect(() => {
            const isIntersecting = localIntersecting || inView;
            updatePlayerStatus(playerIdRef.current, { intersecting: isIntersecting });
        }, [localIntersecting, inView]);

        useEffect(() => {
            if (hlsInstance) {
                if (shouldDownload) {
                    hlsInstance.startLoad();
                } else {
                    hlsInstance.stopLoad();
                }
            }
        }, [shouldDownload, hlsInstance]);

        useEffect(() => {
            let timeoutId;
            if (canLoad && isLoading) {
                timeoutId = setTimeout(() => {
                    setShowSpinner(true);
                }, 300);
            } else {
                setShowSpinner(false);
            }
            return () => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            };
        }, [canLoad, isLoading]);

        useEffect(() => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    setLocalIntersecting(entry.isIntersecting);
                },
                {
                    root: null,
                    rootMargin: "200px 0px",
                    threshold: 0.0,
                }
            );

            if (containerRef.current) {
                observer.observe(containerRef.current);
            }

            return () => {
                observer.disconnect();
            };
        }, []);

        const handleLoadedMetadata = (e) => {
            if (onLoadedMetadata) {
                onLoadedMetadata(e);
            }
            setIsLoading(false);
        };

        const handleCanPlay = () => setIsLoading(false);
        const handlePlaying = () => setIsLoading(false);
        const handleWaiting = () => setIsLoading(true);
        const handleLoadStart = () => setIsLoading(true);

        const handleVideoError = (e) => {
            setIsLoading(false);
        };

        const handlePlay = () => {
            if (playerRef) {
                const playPromise = playerRef.current?.play();
                if (playPromise !== undefined) {
                    playPromise.catch((error) => {});
                }
                playerRef.current.controls = controls
            }
            handleUnmute()
        }


        const handleUnmute = () =>  {
            if (playerRef) {
                playerRef.current.muted = false
            }
        }

        const handleVolumeChange = event => {
            if(!playerRef?.current.muted) {
                setHideCustomUnmute(true)
            }
        }

        useEffect(() => {
            setPlaylist(videoUrl);
        }, [videoUrl]);

        useEffect(() => {
            if (!canLoad) return;

            class FLoader extends Hls.DefaultConfig.loader {
                constructor(config) {
                    super({...config});
                    const load = this.load.bind(this);
                    this.load = (context, config, callbacks) => {
                        if ("manifest" !== context.type) {
                            const newUrl = new URL(playlist);
                            load(
                                {
                                    ...context,
                                    url: `${context.url}${newUrl.search}`,
                                },
                                config,
                                callbacks
                            );
                        } else {
                            load(context, config, callbacks);
                        }
                    };
                }
            }

            let hls;
            if (null !== playerRef && playlist) {
                const video = playerRef?.current;
                if (Hls.isSupported()) {
                    hls = new Hls({
                        loader: FLoader,
                        debug: false,
                        progressive: true,
                        forceKeyFrameOnDiscontinuity: false,
                        testBandwidth: false,
                        autoStartLoad: false,
                    });
                    setHlsInstance(hls);
                    hls.config.maxBufferLength = 5
                    hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                        setHasAudio(data.audio)
                        let startLevelIdx = hls.levels.length - 1;
                        let minDiff = Infinity;
                        for (let i = 0; i < hls.levels.length; i++) {
                            const diff = Math.abs(hls.levels[i].height - 720);
                            if (diff < minDiff) {
                                minDiff = diff;
                                startLevelIdx = i;
                            }
                        }
                        hls.startLevel = startLevelIdx;
                    })
                    hls.loadSource(playlist);
                    hls.attachMedia(video);

                    video.controls = autoPlay && controls;
                    video.loop = loop;
                    video.muted = muted;
                } else {
                    video.setAttribute('src', playlist)
                }
            }
            return () => {
                if (hls) {
                    hls.destroy();
                }
                setHlsInstance(null);
            };
        }, [playlist, playerRef, controls, muted, canLoad, autoPlay, loop]);

        const handleVideoEnded = () => onEnded?.()

        if (Hls.isSupported()) {
            return (
                <VideoControls
                    onPlay={handlePlay}
                    autoPlay={autoPlay}
                    hideUnmute={hideCustomUnmute}
                    onUnmute={handleUnmute}
                    hasAudio={hasAudio}
                >
                    <div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
                        <div className={`${classes.loaderOverlay} ${showSpinner ? "" : classes.loaderHidden}`}>
                            <div className={classes.spinnerRing} />
                        </div>
                        <video
                            onVolumeChange={handleVolumeChange}
                            className={classes.videoPlayer}
                            ref={playerRef}
                            poster={poster}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                            }}
                            onLoadedMetadata={handleLoadedMetadata}
                            onCanPlay={handleCanPlay}
                            onPlaying={handlePlaying}
                            onWaiting={handleWaiting}
                            onLoadStart={handleLoadStart}
                            onError={handleVideoError}
                            onEnded={handleVideoEnded}
                        />
                    </div>
                </VideoControls>
            )
        } else {
            return (
                <VideoControls
                    onPlay={handlePlay}
                    onUnmute={handleUnmute}
                    autoPlay={autoPlay}
                    hideUnmute={hideCustomUnmute}
                    hasAudio={hasAudio}
                >
                    <div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
                        <div className={`${classes.loaderOverlay} ${showSpinner ? "" : classes.loaderHidden}`}>
                            <div className={classes.spinnerRing} />
                        </div>
                        <video
                            onVolumeChange={handleVolumeChange}
                            className={classes.videoPlayer}
                            ref={playerRef}
                            poster={poster}
                            playsInline={true}
                            muted={muted}
                            preload={'auto'}
                            loop={loop}
                            controls={controls}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                            }}
                            onLoadedMetadata={handleLoadedMetadata}
                            onCanPlay={handleCanPlay}
                            onPlaying={handlePlaying}
                            onWaiting={handleWaiting}
                            onLoadStart={handleLoadStart}
                            onError={handleVideoError}
                            onEnded={handleVideoEnded}
                        />
                    </div>
                </VideoControls>
            )
        }
    }
);
